import React, { useState, useEffect, useCallback } from 'react';
import { traineeAPI, agencyAPI, agentAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, formatDate, formatPhoneInput } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, Loader2, GraduationCap, ArrowRight, UserCheck, Search,
  RefreshCw, Calendar, MessageSquare, Users, TrendingUp, Clock, AlertTriangle, Send,
  MessageCircle, Mail, BellOff,
} from 'lucide-react';
import ContactSmsCard from '@/components/sms/ContactSmsCard';

const PIPELINE_STAGES = [
  { key: 'Studying', label: 'Studying', color: '#3b82f6', icon: '📚' },
  { key: 'Exam_Scheduled', label: 'Exam Scheduled', color: '#f59e0b', icon: '📅' },
  { key: 'Exam_Passed', label: 'Exam Passed', color: '#10b981', icon: '✓' },
  { key: 'Licensed', label: 'Licensed', color: '#8b5cf6', icon: '🎓' },
];

const STATUS_COLORS = {
  Studying: 'bg-blue-100 text-blue-700', Exam_Scheduled: 'bg-amber-100 text-amber-700',
  Exam_Passed: 'bg-emerald-100 text-emerald-700', Exam_Failed: 'bg-red-100 text-red-700',
  Licensed: 'bg-violet-100 text-violet-700', Converted: 'bg-sky-100 text-sky-700', Dropped: 'bg-slate-100 text-slate-500',
};

const ALL_STATUSES = ['Studying', 'Exam_Scheduled', 'Exam_Passed', 'Exam_Failed', 'Licensed', 'Converted', 'Dropped'];

const DEFAULT_CHECKLIST = {
  'Enrolled in course': false,
  'Completed study hours': false,
  'Practice exams passed': false,
  'Exam fee paid': false,
  'Background check submitted': false,
  'Exam scheduled': false,
  'Exam passed': false,
  'License application submitted': false,
  'License received': false,
};

const EMPTY_FORM = { first_name: '', last_name: '', email: '', phone: '', agency_id: '', status: 'Studying', language: '', course_name: '', start_date: '', expected_completion: '', exam_date: '', mentor_npn: '', checklist: { ...DEFAULT_CHECKLIST }, notes: '' };

export default function TraineesPage() {
  const { user } = useAuth();
  const isAdmin = isKRRole(user);
  const [trainees, setTrainees] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agents, setAgents] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [convertDialog, setConvertDialog] = useState(null);
  const [convertNpn, setConvertNpn] = useState('');
  const [notesDialog, setNotesDialog] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [smsDialog, setSmsDialog] = useState(null);
  const [view, setView] = useState('pipeline'); // pipeline | table

  useEffect(() => {
    loadData();
    agencyAPI.list().then(r => setAgencies(r.data || [])).catch(() => {});
    agentAPI.list({ page_size: 500 }).then(r => setAgents(r.data?.agents || r.data || [])).catch(() => {});
    traineeAPI.dashboard().then(r => setDashboard(r.data)).catch(() => {});
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await traineeAPI.list({ status: filter === 'all' ? undefined : filter });
      setTrainees(res.data || []);
    } catch { toast.error('Failed to load trainees'); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [filter]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialog(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...EMPTY_FORM, ...t, checklist: t.checklist || { ...DEFAULT_CHECKLIST } }); setDialog(true); };

  const handleSave = async () => {
    if (!form.first_name || !form.last_name) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await traineeAPI.update(editing.trainee_id, form);
        toast.success('Trainee updated');
      } else {
        await traineeAPI.create(form);
        toast.success('Trainee added');
      }
      setDialog(false);
      loadData();
      traineeAPI.dashboard().then(r => setDashboard(r.data)).catch(() => {});
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this trainee?')) return;
    try { await traineeAPI.delete(id); toast.success('Deleted'); loadData(); }
    catch { toast.error('Failed'); }
  };

  const handleConvert = async () => {
    if (!convertNpn.trim()) { toast.error('NPN is required'); return; }
    try {
      await traineeAPI.convertToAgent(convertDialog.trainee_id, convertNpn.trim());
      toast.success(`${convertDialog.first_name} ${convertDialog.last_name} converted to agent!`);
      setConvertDialog(null); setConvertNpn(''); loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to convert'); }
  };

  const openNotes = async (t) => {
    setNotesDialog(t);
    try { const r = await traineeAPI.getNotes(t.trainee_id); setNotes(r.data || []); }
    catch { setNotes([]); }
  };

  const addNote = async () => {
    if (!newNote.trim() || !notesDialog) return;
    try {
      await traineeAPI.addNote(notesDialog.trainee_id, newNote.trim());
      setNewNote('');
      const r = await traineeAPI.getNotes(notesDialog.trainee_id);
      setNotes(r.data || []);
      toast.success('Note added');
    } catch { toast.error('Failed'); }
  };

  const quickStatusChange = async (trainee, newStatus) => {
    try {
      await traineeAPI.update(trainee.trainee_id, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace(/_/g, ' ')}`);
      loadData();
    } catch { toast.error('Failed'); }
  };

  const sendNudge = async (trainee) => {
    try {
      const { data } = await traineeAPI.sendNudge(trainee.trainee_id);
      toast.success(`Motivational email sent to ${data.sent_to}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send email'); }
  };

  const filtered = trainees.filter(t => {
    if (search) {
      const s = search.toLowerCase();
      return `${t.first_name} ${t.last_name}`.toLowerCase().includes(s) || (t.email || '').toLowerCase().includes(s);
    }
    return true;
  });

  const getMentorName = (npn) => {
    const a = agents.find(ag => ag.npn === npn);
    return a ? `${a.first_name} ${a.last_name}` : npn;
  };

  if (!isAdmin) return <div className="flex items-center justify-center h-[60vh]"><p className="text-slate-500">Admin access required.</p></div>;

  return (
    <div className="space-y-4" data-testid="trainees-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">People</p>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Trainees</h1>
          <p className="text-sm text-slate-500 mt-0.5">People studying for their license — track progress to conversion</p>
        </div>
        <div className="flex gap-2">
          <Button variant={view === 'pipeline' ? 'default' : 'outline'} size="sm" onClick={() => setView('pipeline')} className="text-xs">Pipeline</Button>
          <Button variant={view === 'table' ? 'default' : 'outline'} size="sm" onClick={() => setView('table')} className="text-xs">Table</Button>
          <Button variant="outline" size="sm" onClick={loadData}><RefreshCw size={14} /></Button>
          <Button size="sm" onClick={openCreate} className="gap-1.5" data-testid="add-trainee-btn"><Plus size={14} /> Add Trainee</Button>
        </div>
      </div>

      {/* Dashboard Metrics */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-1.5 text-blue-600 mb-1"><Users size={14} /><span className="text-[10px] font-medium uppercase">Active</span></div>
              <p className="text-xl font-bold text-blue-700">{dashboard.total_active}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-1.5 text-amber-600 mb-1"><Calendar size={14} /><span className="text-[10px] font-medium uppercase">Exams (14d)</span></div>
              <p className="text-xl font-bold text-amber-700">{dashboard.upcoming_exams}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-1.5 text-red-600 mb-1"><AlertTriangle size={14} /><span className="text-[10px] font-medium uppercase">Overdue</span></div>
              <p className="text-xl font-bold text-red-700">{dashboard.overdue}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-1.5 text-emerald-600 mb-1"><TrendingUp size={14} /><span className="text-[10px] font-medium uppercase">Converted</span></div>
              <p className="text-xl font-bold text-emerald-700">{dashboard.total_converted}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-violet-50 to-white border-violet-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-1.5 text-violet-600 mb-1"><GraduationCap size={14} /><span className="text-[10px] font-medium uppercase">Pass Rate</span></div>
              <p className="text-xl font-bold text-violet-700">{dashboard.conversion_rate}%</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 h-8 text-xs" />
        </div>
        {view === 'table' && (
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem>{ALL_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
      ) : view === 'pipeline' ? (
        /* ═══════ PIPELINE VIEW ═══════ */
        <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: '50vh' }}>
          {PIPELINE_STAGES.map(stage => {
            const cards = filtered.filter(t => t.status === stage.key);
            return (
              <div key={stage.key} className="flex-shrink-0 w-72" data-testid={`pipeline-${stage.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
                  <h3 className="text-sm font-semibold text-slate-700">{stage.label}</h3>
                  <Badge variant="secondary" className="text-[10px] h-5 px-1.5 ml-auto">{cards.length}</Badge>
                </div>
                <div className="space-y-2 min-h-[200px]">
                  {cards.length === 0 ? (
                    <div className="text-center py-8 text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg">No trainees</div>
                  ) : cards.map(t => {
                    const checklist = t.checklist || {};
                    const checkCount = Object.values(checklist).filter(Boolean).length;
                    const checkTotal = Object.keys(checklist).length;
                    const isOverdue = t.expected_completion && t.expected_completion < new Date().toISOString().split('T')[0];
                    return (
                      <div key={t.trainee_id} className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-all">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="text-sm font-medium text-slate-800">{t.first_name} {t.last_name}</p>
                            {t.course_name && <p className="text-[11px] text-slate-500">{t.course_name}</p>}
                          </div>
                          <div className="flex gap-0.5">
                            {t.phone && <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-sky-600" onClick={() => setSmsDialog(t)} title="Send SMS" data-testid={`trainee-sms-btn-${t.trainee_id}`}><MessageCircle size={12} /></Button>}
                            {t.email && !t.prelicensing_opt_out && <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-emerald-600" onClick={() => sendNudge(t)} title="Send motivational email" data-testid={`trainee-nudge-btn-${t.trainee_id}`}><Mail size={12} /></Button>}
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400" onClick={() => openNotes(t)}><MessageSquare size={12} /></Button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400" onClick={() => openEdit(t)}><Edit2 size={12} /></Button>
                          </div>
                        </div>
                        {/* Checklist progress */}
                        {checkTotal > 0 && (
                          <div className="mb-2">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-[10px] text-slate-400">Progress</span>
                              <span className="text-[10px] font-medium text-slate-500">{checkCount}/{checkTotal}</span>
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${(checkCount/checkTotal)*100}%`, backgroundColor: stage.color }} />
                            </div>
                          </div>
                        )}
                        {/* Meta */}
                        <div className="flex items-center justify-between text-[10px] text-slate-400">
                          {t.exam_date && <span className="flex items-center gap-0.5"><Calendar size={10} /> Exam: {t.exam_date}</span>}
                          {t.mentor_npn && <span>Mentor: {getMentorName(t.mentor_npn).split(' ')[0]}</span>}
                          {isOverdue && !t.exam_date && <span className="text-red-500 font-semibold flex items-center gap-0.5"><AlertTriangle size={10} /> Overdue</span>}
                        </div>
                        {/* Quick actions */}
                        {stage.key === 'Studying' && (
                          <Button variant="ghost" size="sm" className="w-full mt-2 h-6 text-[10px] text-amber-600" onClick={() => quickStatusChange(t, 'Exam_Scheduled')}>
                            <ArrowRight size={10} className="mr-1" /> Schedule Exam
                          </Button>
                        )}
                        {stage.key === 'Exam_Scheduled' && (
                          <div className="flex gap-1 mt-2">
                            <Button variant="ghost" size="sm" className="flex-1 h-6 text-[10px] text-emerald-600" onClick={() => quickStatusChange(t, 'Exam_Passed')}>Passed</Button>
                            <Button variant="ghost" size="sm" className="flex-1 h-6 text-[10px] text-red-500" onClick={() => quickStatusChange(t, 'Exam_Failed')}>Failed</Button>
                          </div>
                        )}
                        {stage.key === 'Exam_Passed' && (
                          <Button variant="ghost" size="sm" className="w-full mt-2 h-6 text-[10px] text-violet-600" onClick={() => quickStatusChange(t, 'Licensed')}>
                            <ArrowRight size={10} className="mr-1" /> Mark Licensed
                          </Button>
                        )}
                        {stage.key === 'Licensed' && (
                          <Button variant="ghost" size="sm" className="w-full mt-2 h-6 text-[10px] text-emerald-600" onClick={() => { setConvertDialog(t); setConvertNpn(''); }}>
                            <UserCheck size={10} className="mr-1" /> Convert to Agent
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ═══════ TABLE VIEW ═══════ */
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="trainees-table">
              <thead><tr className="border-b bg-slate-50 text-xs text-slate-500 uppercase text-left">
                <th className="px-3 py-2.5">Name</th><th className="px-3 py-2.5">Email</th><th className="px-3 py-2.5">Course</th><th className="px-3 py-2.5">Status</th><th className="px-3 py-2.5">Exam Date</th><th className="px-3 py-2.5">Mentor</th><th className="px-3 py-2.5">Progress</th><th className="px-3 py-2.5 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map(t => {
                  const cl = t.checklist || {};
                  const done = Object.values(cl).filter(Boolean).length;
                  const total = Object.keys(cl).length;
                  return (
                    <tr key={t.trainee_id} className={`border-b border-slate-100 hover:bg-slate-50/50 ${t.status === 'Converted' ? 'opacity-50' : ''}`}>
                      <td className="px-3 py-2"><p className="font-medium text-slate-800 text-xs">{t.first_name} {t.last_name}</p></td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        <span>{t.email || '-'}</span>
                        {t.prelicensing_opt_out && (
                          <span className="inline-flex items-center gap-0.5 ml-1.5 text-[10px] text-amber-600" title="Opted out of Pre-Licensing emails"><BellOff size={10} /> opted out</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">{t.course_name || '-'}</td>
                      <td className="px-3 py-2"><Badge className={`text-[10px] ${STATUS_COLORS[t.status]}`}>{t.status?.replace(/_/g, ' ')}</Badge></td>
                      <td className="px-3 py-2 text-xs text-slate-500 font-mono">{t.exam_date || '-'}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{t.mentor_npn ? getMentorName(t.mentor_npn) : '-'}</td>
                      <td className="px-3 py-2"><span className="text-xs text-slate-500">{total > 0 ? `${done}/${total}` : '-'}</span></td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex gap-0.5 justify-end">
                          {t.phone && <Button variant="ghost" size="sm" onClick={() => setSmsDialog(t)} className="h-7 w-7 p-0 text-slate-400 hover:text-sky-600" title="Send SMS" data-testid={`trainee-row-sms-${t.trainee_id}`}><MessageCircle size={14} /></Button>}
                          {t.email && !t.prelicensing_opt_out && t.status !== 'Converted' && t.status !== 'Dropped' && <Button variant="ghost" size="sm" onClick={() => sendNudge(t)} className="h-7 w-7 p-0 text-slate-400 hover:text-emerald-600" title="Send motivational email" data-testid={`trainee-row-nudge-${t.trainee_id}`}><Mail size={14} /></Button>}
                          <Button variant="ghost" size="sm" onClick={() => openNotes(t)} className="h-7 w-7 p-0 text-slate-400"><MessageSquare size={14} /></Button>
                          {(t.status === 'Licensed' || t.status === 'Exam_Passed') && <Button variant="ghost" size="sm" onClick={() => { setConvertDialog(t); setConvertNpn(''); }} className="h-7 px-2 text-xs gap-1 text-emerald-600"><UserCheck size={13} /></Button>}
                          <Button variant="ghost" size="sm" onClick={() => openEdit(t)} className="h-7 w-7 p-0 text-slate-400"><Edit2 size={14} /></Button>
                          {t.status !== 'Converted' && <Button variant="ghost" size="sm" onClick={() => handleDelete(t.trainee_id)} className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"><Trash2 size={14} /></Button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No trainees found</div>}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="trainee-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit Trainee' : 'Add Trainee'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">First Name *</Label><Input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className="h-8 text-xs" /></div>
              <div><Label className="text-xs">Last Name *</Label><Input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} className="h-8 text-xs" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-8 text-xs" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={formatPhoneInput(form.phone)} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="h-8 text-xs" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Agency</Label>
                <Select value={form.agency_id || 'none'} onValueChange={v => setForm(f => ({ ...f, agency_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">-</SelectItem>{agencies.map(a => <SelectItem key={a.agency_id} value={a.agency_id}>{a.name || a.agency_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{ALL_STATUSES.filter(s => s !== 'Converted').map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Mentor (Agent)</Label>
                <Select value={form.mentor_npn || 'none'} onValueChange={v => setForm(f => ({ ...f, mentor_npn: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">-</SelectItem>{agents.slice(0, 50).map(a => <SelectItem key={a.npn} value={a.npn}>{a.first_name} {a.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Email Language</Label>
                <Select value={form.language || 'auto'} onValueChange={v => setForm(f => ({ ...f, language: v === 'auto' ? '' : v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="trainee-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Bilingual (ES + EN)</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Course</Label><Input value={form.course_name} onChange={e => setForm(f => ({ ...f, course_name: e.target.value }))} className="h-8 text-xs" /></div>
              <div><Label className="text-xs">Expected Completion</Label><Input type="date" value={form.expected_completion} onChange={e => setForm(f => ({ ...f, expected_completion: e.target.value }))} className="h-8 text-xs" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Exam Date</Label><Input type="date" value={form.exam_date} onChange={e => setForm(f => ({ ...f, exam_date: e.target.value }))} className="h-8 text-xs" /></div>
            </div>
            {/* Checklist */}
            <div>
              <Label className="text-xs font-semibold text-slate-600 mb-2 block">Requirements Checklist</Label>
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(form.checklist || DEFAULT_CHECKLIST).map(item => (
                  <div key={item} className="flex items-center gap-2">
                    <Checkbox checked={!!form.checklist?.[item]} onCheckedChange={v => setForm(f => ({ ...f, checklist: { ...f.checklist, [item]: !!v } }))} id={`cl-${item}`} />
                    <Label htmlFor={`cl-${item}`} className="text-xs font-normal cursor-pointer text-slate-600">{item}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="text-xs" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}{editing ? 'Update' : 'Add Trainee'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={!!notesDialog} onOpenChange={o => { if (!o) setNotesDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="trainee-notes-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">Notes: {notesDialog?.first_name} {notesDialog?.last_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {notes.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No notes yet</p>
            ) : notes.map(n => (
              <div key={n.note_id} className="p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                <p className="text-sm text-slate-700">{n.content}</p>
                <p className="text-[10px] text-slate-400 mt-1">{n.created_by_name} — {formatDate(n.created_at)}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-2 border-t">
            <Input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..." className="h-8 text-xs" onKeyDown={e => e.key === 'Enter' && addNote()} />
            <Button size="sm" onClick={addNote} className="h-8 gap-1"><Send size={12} /> Add</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog open={!!convertDialog} onOpenChange={o => { if (!o) setConvertDialog(null); }}>
        <DialogContent className="max-w-sm" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><UserCheck size={18} className="text-emerald-500" /> Convert to Agent</DialogTitle>
            <DialogDescription>Convert <strong>{convertDialog?.first_name} {convertDialog?.last_name}</strong> to a full agent.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold">NPN (National Producer Number) *</Label>
              <Input value={convertNpn} onChange={e => setConvertNpn(e.target.value)} placeholder="Enter NPN..." className="mt-1" data-testid="convert-npn-input" />
              <p className="text-[10px] text-slate-400 mt-1">Name, email, phone, and agency will be migrated.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialog(null)}>Cancel</Button>
            <Button onClick={handleConvert} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="convert-confirm-btn"><ArrowRight size={14} /> Convert</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* SMS Dialog - Uses Agent messaging service (813 number) */}
      <Dialog open={!!smsDialog} onOpenChange={o => { if (!o) setSmsDialog(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="trainee-sms-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base flex items-center gap-2">
              <MessageCircle size={16} className="text-sky-500" />
              SMS: {smsDialog?.first_name} {smsDialog?.last_name}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Messages are sent through the Agent channel (+1 813 701-2300) — the same number used for licensed agents.
            </DialogDescription>
          </DialogHeader>
          {smsDialog && (
            <ContactSmsCard
              contactType="trainee"
              contactId={smsDialog.trainee_id}
              phone={smsDialog.phone}
            />
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
